// ===========================================
// Dev Profile Page - Developer Activity & Profile
// ===========================================
// Features:
// - GitHub-style activity calendar based on Cursor usage
// - Cost data sources management (subscriptions + Cursor CSV)
// - Profile editing (username, bio, location, tech preferences)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI, analyticsAPI } from '../services/api';
import { 
  MapPin, Loader, Plus, Trash2, Check, X,
  Globe, Bot, Monitor, Cloud, Database, Activity, HardDrive, MoreHorizontal,
  CheckCircle2, AlertCircle, ChevronDown, ChevronUp, Zap, Calendar, Pause, Play,
  Github, Linkedin, Camera  // Social links & profile picture icons
} from 'lucide-react';
import CursorUsageUpload from '../components/CursorUsageUpload';
import SubscriptionModal from '../components/SubscriptionModal';
import './DevProfile.scss';

// ===========================================
// Subscription Type Icons Mapping
// ===========================================
const typeIcons = {
  ide: Monitor,
  ai: Bot,
  hosting: Cloud,
  database: Database,
  monitoring: Activity,
  storage: HardDrive,
  other: MoreHorizontal
};

// ===========================================
// Inline Editable Field Component
// ===========================================
// Click-to-edit field that auto-saves on blur or Enter
const InlineField = ({ 
  value, 
  placeholder, 
  onSave, 
  type = 'text',
  multiline = false,
  icon: Icon = null,
  className = ''
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [cancelled, setCancelled] = useState(false);  // Track if user pressed Escape
  const inputRef = useRef(null);

  // Update local value when prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || '');
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Save and exit edit mode
  const handleSave = () => {
    setIsEditing(false);
    setCancelled(false);
    if (editValue !== value) {
      onSave(editValue);
    }
  };

  // Cancel edit and revert value
  const handleCancel = () => {
    setCancelled(true);  // Mark as cancelled so onBlur doesn't save
    setIsEditing(false);
    setEditValue(value || '');
  };

  // Handle blur - only save if not cancelled via Escape
  const handleBlur = () => {
    if (!cancelled) {
      handleSave();
    }
    setCancelled(false);  // Reset cancelled state
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Render editing input
  if (isEditing) {
    const InputComponent = multiline ? 'textarea' : 'input';
    return (
      <div className={`inline-field inline-field--editing ${className}`}>
        {Icon && <Icon size={12} className="inline-field__icon" />}
        <InputComponent
          ref={inputRef}
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="inline-field__input"
          rows={multiline ? 2 : undefined}
        />
      </div>
    );
  }

  // Render display value (click to edit)
  const isEmpty = !value || value.trim() === '';
  
  return (
    <div 
      className={`inline-field ${isEmpty ? 'inline-field--empty' : ''} ${className}`}
      onClick={() => setIsEditing(true)}
      title="Click to edit"
    >
      {Icon && <Icon size={12} className="inline-field__icon" />}
      <span className="inline-field__value">
        {isEmpty ? placeholder : value}
      </span>
    </div>
  );
};

// ===========================================
// Activity Calendar Component
// ===========================================
// GitHub-style contribution calendar showing Cursor AI usage intensity
const ActivityCalendar = ({ activityData, loading, metric = 'cost' }) => {
  // Generate dates for the past year (52 weeks + current partial week)
  const generateCalendarDates = () => {
    const today = new Date();
    const dates = [];
    
    // Start from the first day of the week, ~1 year ago
    const startDate = new Date(today);
    startDate.setFullYear(startDate.getFullYear() - 1);
    // Adjust to start of week (Sunday)
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    // Generate all dates from start to today
    const currentDate = new Date(startDate);
    while (currentDate <= today) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Create a map of date -> activity data for quick lookup
  const activityMap = {};
  if (activityData) {
    activityData.forEach(item => {
      activityMap[item.date] = item;
    });
  }

  // Calculate intensity level (0-4) based on metric value
  const getIntensityLevel = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const data = activityMap[dateStr];
    
    if (!data) return 0;
    
    const value = metric === 'cost' ? data.totalCost : data.totalTokens;
    if (!value || value === 0) return 0;
    
    // Calculate thresholds based on max value in dataset
    const allValues = activityData
      .map(d => metric === 'cost' ? d.totalCost : d.totalTokens)
      .filter(v => v > 0);
    
    if (allValues.length === 0) return 0;
    
    const maxValue = Math.max(...allValues);
    const percentage = value / maxValue;
    
    // 4 intensity levels
    if (percentage >= 0.75) return 4;
    if (percentage >= 0.5) return 3;
    if (percentage >= 0.25) return 2;
    return 1;
  };

  // Get tooltip content for a date
  const getTooltip = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const data = activityMap[dateStr];
    
    const formattedDate = date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    
    if (!data) {
      return `${formattedDate}\nNo activity`;
    }
    
    return `${formattedDate}\n$${data.totalCost?.toFixed(2) || '0.00'} spent\n${(data.totalTokens || 0).toLocaleString()} tokens\n${data.eventCount || 0} requests`;
  };

  const dates = generateCalendarDates();
  
  // Group dates by week for rendering
  const weeks = [];
  let currentWeek = [];
  
  dates.forEach((date, index) => {
    currentWeek.push(date);
    if (date.getDay() === 6 || index === dates.length - 1) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });

  // Month labels for header
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Get month label positions
  const getMonthLabels = () => {
    const labels = [];
    let lastMonth = -1;
    
    weeks.forEach((week, weekIndex) => {
      const firstDayOfWeek = week[0];
      const month = firstDayOfWeek.getMonth();
      
      if (month !== lastMonth) {
        labels.push({ month: months[month], weekIndex });
        lastMonth = month;
      }
    });
    
    return labels;
  };

  const monthLabels = getMonthLabels();

  // Calculate totals for the period
  const totals = activityData?.reduce((acc, item) => ({
    cost: acc.cost + (item.totalCost || 0),
    tokens: acc.tokens + (item.totalTokens || 0),
    events: acc.events + (item.eventCount || 0)
  }), { cost: 0, tokens: 0, events: 0 }) || { cost: 0, tokens: 0, events: 0 };

  if (loading) {
    return (
      <div className="activity-calendar activity-calendar--loading">
        <Loader className="animate-spin" size={24} />
        <span>Loading activity data...</span>
      </div>
    );
  }

  return (
    <div className="activity-calendar">
      {/* Calendar Header with Totals */}
      <div className="activity-calendar__header">
        <div className="activity-calendar__title">
          <Calendar size={18} />
          <h3>AI Development Activity</h3>
        </div>
        <div className="activity-calendar__stats">
          <span className="activity-calendar__stat">
            <strong>${totals.cost.toFixed(2)}</strong> total spend
          </span>
          <span className="activity-calendar__stat">
            <strong>{totals.tokens.toLocaleString()}</strong> tokens
          </span>
          <span className="activity-calendar__stat">
            <strong>{totals.events.toLocaleString()}</strong> requests
          </span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="activity-calendar__container">
        {/* Month Labels */}
        <div className="activity-calendar__months">
          {monthLabels.map((label, i) => (
            <span 
              key={i} 
              className="activity-calendar__month"
              style={{ gridColumn: label.weekIndex + 2 }}
            >
              {label.month}
            </span>
          ))}
        </div>

        {/* Day Labels */}
        <div className="activity-calendar__days">
          <span></span>
          <span>Mon</span>
          <span></span>
          <span>Wed</span>
          <span></span>
          <span>Fri</span>
          <span></span>
        </div>

        {/* Grid of weeks */}
        <div className="activity-calendar__grid">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="activity-calendar__week">
              {/* Pad start of first week if needed */}
              {weekIndex === 0 && week[0].getDay() > 0 && (
                Array(week[0].getDay()).fill(null).map((_, i) => (
                  <div key={`pad-${i}`} className="activity-calendar__day activity-calendar__day--empty" />
                ))
              )}
              {week.map((date, dayIndex) => (
                <div 
                  key={dayIndex}
                  className={`activity-calendar__day activity-calendar__day--level-${getIntensityLevel(date)}`}
                  title={getTooltip(date)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="activity-calendar__legend">
        <span className="activity-calendar__legend-label">Less</span>
        <div className="activity-calendar__day activity-calendar__day--level-0" />
        <div className="activity-calendar__day activity-calendar__day--level-1" />
        <div className="activity-calendar__day activity-calendar__day--level-2" />
        <div className="activity-calendar__day activity-calendar__day--level-3" />
        <div className="activity-calendar__day activity-calendar__day--level-4" />
        <span className="activity-calendar__legend-label">More</span>
      </div>
    </div>
  );
};

// ===========================================
// Main DevProfile Component
// ===========================================
const DevProfile = () => {
  const { user, refreshUser, updateProfile } = useAuth();
  
  // ===========================================
  // State Management
  // ===========================================
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Activity calendar data
  const [activityData, setActivityData] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  
  // Analytics data for Cost Data Sources section
  const [analyticsData, setAnalyticsData] = useState(null);
  
  // Cursor usage state (for Cost Data Sources)
  const [cursorUsage, setCursorUsage] = useState(null);
  
  // Cost Data Sources accordion state
  const [isDataSourcesExpanded, setIsDataSourcesExpanded] = useState(true);
  
  // Subscription modal state
  const [showSubModal, setShowSubModal] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  
  // Inline editing state for subscription fields (Started, Cost)
  const [inlineEdit, setInlineEdit] = useState({ id: null, field: null, value: '' });
  
  // Profile data (synced with user, updated via inline edits)
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    location: user?.location || '',
    skills: user?.skills || [],
    githubUrl: user?.githubUrl || '',
    linkedinUrl: user?.linkedinUrl || '',
    profilePicture: user?.profilePicture || null
  });
  
  // Tech tag input state (for adding new tags)
  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  
  // Profile picture upload state
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef(null);  // Hidden file input for picture upload
  
  // Reference for scroll to upload
  const uploadSectionRef = useRef(null);

  // ===========================================
  // Data Fetching
  // ===========================================
  
  // Fetch activity calendar data
  const fetchActivityData = useCallback(async () => {
    setActivityLoading(true);
    try {
      const response = await authAPI.getCursorActivityCalendar();
      setActivityData(response.data.data.dailyActivity || []);
    } catch (error) {
      console.error('Failed to fetch activity data:', error);
      setActivityData([]);
    }
    setActivityLoading(false);
  }, []);

  // Fetch cursor usage summary
  const fetchCursorUsage = useCallback(async () => {
    try {
      const response = await authAPI.getCursorUsage({});
      setCursorUsage(response.data.data.cursorUsage);
    } catch (error) {
      console.error('Failed to fetch cursor usage:', error);
      setCursorUsage(null);
    }
  }, []);

  // Fetch analytics overview for active project count
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await analyticsAPI.getAll();
      setAnalyticsData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([
        fetchActivityData(),
        fetchCursorUsage(),
        fetchAnalytics()
      ]);
      setLoading(false);
    };
    loadAllData();
  }, [fetchActivityData, fetchCursorUsage, fetchAnalytics]);

  // Sync profile data with user object when it changes
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        bio: user.bio || '',
        location: user.location || '',
        skills: user.skills || [],
        githubUrl: user.githubUrl || '',
        linkedinUrl: user.linkedinUrl || '',
        profilePicture: user.profilePicture || null
      });
    }
  }, [user]);

  // ===========================================
  // Profile Handlers - Inline Field Saves
  // ===========================================
  
  // Save a single field via inline edit (auto-saves on blur/enter)
  const handleFieldSave = async (field, value) => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    // Update local state immediately for responsiveness
    const updatedData = { ...profileData, [field]: value };
    setProfileData(updatedData);
    
    try {
      const result = await updateProfile(updatedData);
      
      if (result.success) {
        // Brief success indicator
        setMessage({ type: 'success', text: 'Saved' });
        setTimeout(() => setMessage({ type: '', text: '' }), 1500);
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save' });
    }
    
    setSaving(false);
  };

  // Add tech skill tag and auto-save
  const handleAddTag = async (e) => {
    if (e) e.preventDefault();
    const tag = tagInput.trim().toLowerCase();
    
    if (tag && !profileData.skills.includes(tag)) {
      const newSkills = [...profileData.skills, tag];
      setTagInput('');
      setShowTagInput(false);
      await handleFieldSave('skills', newSkills);
    }
  };

  // Remove tech skill tag and auto-save
  const handleRemoveTag = async (tagToRemove) => {
    const newSkills = profileData.skills.filter(tag => tag !== tagToRemove);
    await handleFieldSave('skills', newSkills);
  };

  // ===========================================
  // Profile Picture Upload Handler
  // ===========================================
  // Handles image selection, validation, and auto-saves
  const handlePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type - must be an image
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }
    
    // Validate file size - max 2MB to keep base64 string reasonable
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image must be less than 2MB' });
      return;
    }
    
    setUploadingPicture(true);
    
    // Convert image to base64 data URL for storage
    const reader = new FileReader();
    reader.onloadend = async () => {
      await handleFieldSave('profilePicture', reader.result);
      setUploadingPicture(false);
    };
    reader.onerror = () => {
      setMessage({ type: 'error', text: 'Failed to read image file' });
      setUploadingPicture(false);
    };
    reader.readAsDataURL(file);
  };

  // Remove profile picture and auto-save
  const handleRemovePicture = async () => {
    await handleFieldSave('profilePicture', null);
  };

  // ===========================================
  // Subscription Management Handlers
  // ===========================================
  
  const handleAddSubscription = () => {
    setEditingSub(null);
    setShowSubModal(true);
  };

  const handleSaveSubscription = async (subData) => {
    try {
      if (editingSub) {
        await authAPI.updateSubscription(editingSub._id, subData);
      } else {
        await authAPI.addSubscription(subData);
      }
      
      await Promise.all([refreshUser(), fetchAnalytics()]);
      return { success: true };
    } catch (error) {
      console.error('Failed to save subscription:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to save subscription' };
    }
  };

  const handleDeleteSubscription = async (subId) => {
    if (!window.confirm('Delete this subscription?')) return;
    
    try {
      await authAPI.deleteSubscription(subId);
      await Promise.all([refreshUser(), fetchAnalytics()]);
    } catch (error) {
      console.error('Failed to delete subscription:', error);
    }
  };

  const handleToggleSubscription = async (sub) => {
    try {
      // Use the subscription update API to toggle isActive status
      await authAPI.updateSubscription(sub._id, { isActive: !sub.isActive });
      await refreshUser();
    } catch (error) {
      console.error('Failed to toggle subscription:', error);
    }
  };

  // ===========================================
  // Inline Edit Handlers (for Started & Cost)
  // ===========================================
  
  // Start inline editing a field
  const handleInlineEditStart = (sub, field) => {
    const value = field === 'startDate' 
      ? (sub.startDate ? new Date(sub.startDate).toISOString().split('T')[0] : '')
      : sub.monthlyCost;
    setInlineEdit({ id: sub._id, field, value });
  };

  // Save inline edit to database
  const handleInlineEditSave = async (sub) => {
    if (!inlineEdit.id || !inlineEdit.field) return;
    
    try {
      const updateData = { ...sub };
      if (inlineEdit.field === 'startDate') {
        updateData.startDate = inlineEdit.value || null;
      } else if (inlineEdit.field === 'monthlyCost') {
        updateData.monthlyCost = parseFloat(inlineEdit.value) || 0;
      }
      
      await authAPI.updateSubscription(sub._id, updateData);
      await refreshUser();
      setInlineEdit({ id: null, field: null, value: '' });
    } catch (error) {
      console.error('Failed to update subscription:', error);
    }
  };

  // Cancel inline edit without saving
  const handleInlineEditCancel = () => {
    setInlineEdit({ id: null, field: null, value: '' });
  };

  // Handle data refresh after CSV upload
  const handleDataRefresh = async () => {
    await Promise.all([
      fetchActivityData(),
      fetchCursorUsage(),
      fetchAnalytics()
    ]);
  };

  // ===========================================
  // Helper Functions
  // ===========================================
  
  const getLastUploadText = () => {
    if (!cursorUsage?.lastUploadedAt) return null;
    
    const uploaded = new Date(cursorUsage.lastUploadedAt);
    const now = new Date();
    const diffMs = now - uploaded;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return uploaded.toLocaleDateString();
  };

  // Calculate active subscription count
  const activeSubscriptions = user?.globalSubscriptions?.filter(s => s.isActive) || [];
  const activeSubCount = activeSubscriptions.length;

  // ===========================================
  // Loading State
  // ===========================================
  if (loading) {
    return (
      <div className="dev-profile dev-profile--loading">
        <Loader className="animate-spin" size={32} />
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="dev-profile">
      {/* ===========================================
          Header Section - Tactical Terminal Style
          =========================================== */}
      <header className="dev-profile__header">
        <h1>Dev Profile</h1>
        <p>Your developer activity and cost data sources.</p>
      </header>

      {/* ===========================================
          Profile Card Section - Inline Editing
          =========================================== */}
      <section className="dev-profile__card">
        {/* Main profile row: avatar + info */}
        <div className="dev-profile__profile-row">
          {/* Avatar - click to upload */}
          <div 
            className="dev-profile__avatar-wrapper"
            onClick={() => fileInputRef.current?.click()}
            title="Click to change photo"
          >
            {profileData.profilePicture ? (
              <>
                <img 
                  src={profileData.profilePicture} 
                  alt={profileData.name || 'Profile'} 
                  className="dev-profile__avatar-img"
                />
                <button 
                  type="button"
                  className="dev-profile__avatar-remove"
                  onClick={(e) => { e.stopPropagation(); handleRemovePicture(); }}
                  title="Remove photo"
                >
                  <X size={10} />
                </button>
              </>
            ) : (
              <div className="dev-profile__avatar">
                {uploadingPicture ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Camera size={16} />
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePictureUpload}
              style={{ display: 'none' }}
            />
          </div>
          
          {/* Profile info - all inline editable */}
          <div className="dev-profile__info">
            {/* Name - inline editable */}
            <InlineField
              value={profileData.name}
              placeholder="Your name"
              onSave={(val) => handleFieldSave('name', val)}
              className="dev-profile__name-field"
            />
            
            {/* Bio - inline editable (multiline) */}
            <InlineField
              value={profileData.bio}
              placeholder="Add a short bio..."
              onSave={(val) => handleFieldSave('bio', val)}
              multiline
              className="dev-profile__bio-field"
            />
            
            {/* Meta row: location, email, social links */}
            <div className="dev-profile__meta">
              {/* Location - inline editable */}
              <InlineField
                value={profileData.location}
                placeholder="Add location"
                onSave={(val) => handleFieldSave('location', val)}
                icon={MapPin}
                className="dev-profile__location-field"
              />
              
              <span className="dev-profile__email">{user?.email}</span>
              
              {/* GitHub - inline editable or link */}
              <div className="dev-profile__social-item">
                {profileData.githubUrl ? (
                  <a 
                    href={profileData.githubUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="dev-profile__social-link"
                    title="GitHub"
                  >
                    <Github size={12} />
                  </a>
                ) : null}
                <InlineField
                  value={profileData.githubUrl}
                  placeholder={profileData.githubUrl ? '' : 'GitHub'}
                  onSave={(val) => handleFieldSave('githubUrl', val)}
                  icon={profileData.githubUrl ? null : Github}
                  className={`dev-profile__github-field ${profileData.githubUrl ? 'dev-profile__github-field--hidden' : ''}`}
                />
              </div>
              
              {/* LinkedIn - inline editable or link */}
              <div className="dev-profile__social-item">
                {profileData.linkedinUrl ? (
                  <a 
                    href={profileData.linkedinUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="dev-profile__social-link"
                    title="LinkedIn"
                  >
                    <Linkedin size={12} />
                  </a>
                ) : null}
                <InlineField
                  value={profileData.linkedinUrl}
                  placeholder={profileData.linkedinUrl ? '' : 'LinkedIn'}
                  onSave={(val) => handleFieldSave('linkedinUrl', val)}
                  icon={profileData.linkedinUrl ? null : Linkedin}
                  className={`dev-profile__linkedin-field ${profileData.linkedinUrl ? 'dev-profile__linkedin-field--hidden' : ''}`}
                />
              </div>
            </div>
          </div>
          
          {/* Save indicator */}
          {saving && (
            <div className="dev-profile__saving">
              <Loader size={12} className="animate-spin" />
            </div>
          )}
        </div>

        {/* Tech tags section */}
        <div className="dev-profile__tech-tags">
          <div className="dev-profile__tags">
            {profileData.skills.map((tag, index) => (
              <span key={index} className="dev-profile__tag">
                {tag}
                <button 
                  type="button" 
                  onClick={() => handleRemoveTag(tag)}
                  className="dev-profile__tag-remove"
                  title="Remove"
                >
                  ×
                </button>
              </span>
            ))}
            
            {/* Add tag inline */}
            {showTagInput ? (
              <div className="dev-profile__tag-input-inline">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Tag name"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddTag(e);
                    if (e.key === 'Escape') { setShowTagInput(false); setTagInput(''); }
                  }}
                  onBlur={() => { if (!tagInput.trim()) setShowTagInput(false); }}
                />
                <button onClick={handleAddTag} className="dev-profile__tag-add-btn">
                  <Check size={10} />
                </button>
              </div>
            ) : (
              <button 
                className="dev-profile__tag dev-profile__tag--add"
                onClick={() => setShowTagInput(true)}
              >
                <Plus size={10} /> Add skill
              </button>
            )}
          </div>
        </div>

        {/* Message Display - brief feedback */}
        {message.text && (
          <div className={`dev-profile__message dev-profile__message--${message.type}`}>
            {message.type === 'success' ? <Check size={10} /> : <AlertCircle size={10} />}
            {message.text}
          </div>
        )}
      </section>

      {/* ===========================================
          Activity Calendar Section
          =========================================== */}
      <section className="dev-profile__section">
        <ActivityCalendar 
          activityData={activityData} 
          loading={activityLoading}
          metric="cost"
        />
      </section>

      {/* ===========================================
          Cost Data Sources - Collapsible Accordion
          =========================================== */}
      <section className={`dev-profile__data-sources ${isDataSourcesExpanded ? 'dev-profile__data-sources--expanded' : ''}`}>
        {/* Accordion Header */}
        <button 
          className="dev-profile__data-sources-toggle"
          onClick={() => setIsDataSourcesExpanded(!isDataSourcesExpanded)}
          aria-expanded={isDataSourcesExpanded}
        >
          <div className="dev-profile__data-sources-toggle-left">
            <Zap size={16} />
            <h2>Cost Data Sources</h2>
          </div>
          
          {/* Collapsed State: Show summary badges */}
          {!isDataSourcesExpanded && (
            <div className="dev-profile__data-sources-summary">
              <div className={`dev-profile__summary-badge ${activeSubCount > 0 ? 'dev-profile__summary-badge--ok' : 'dev-profile__summary-badge--warning'}`}>
                {activeSubCount > 0 ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                <span>{activeSubCount > 0 ? `${activeSubCount} active` : 'No subscriptions'}</span>
              </div>
              
              <div className={`dev-profile__summary-badge ${cursorUsage?.lastUploadedAt ? 'dev-profile__summary-badge--ok' : 'dev-profile__summary-badge--warning'}`}>
                {cursorUsage?.lastUploadedAt ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                <span>{cursorUsage?.lastUploadedAt ? `Cursor: ${getLastUploadText()}` : 'No Cursor data'}</span>
              </div>
            </div>
          )}
          
          {/* Expanded hint */}
          {isDataSourcesExpanded && (
            <span className="dev-profile__data-sources-hint">
              Configure your cost inputs to power the analytics below
            </span>
          )}
          
          <div className="dev-profile__data-sources-chevron">
            {isDataSourcesExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </button>
        
        {/* Accordion Content */}
        <div className={`dev-profile__data-sources-content ${isDataSourcesExpanded ? 'dev-profile__data-sources-content--visible' : ''}`}>
          <div className="dev-profile__data-sources-grid">
            {/* Fixed Subscriptions Card */}
            <div className="dev-profile__source-card">
              <div className="dev-profile__source-card-header">
                <Globe size={16} />
                <h4>Fixed Subscriptions</h4>
                {activeSubCount > 0 ? (
                  <span className="dev-profile__source-status dev-profile__source-status--active">
                    ✓ {activeSubCount} active
                  </span>
                ) : (
                  <span className="dev-profile__source-status dev-profile__source-status--missing">
                    ⚠ No data
                  </span>
                )}
              </div>
              
              <p className="dev-profile__source-description">
                Monthly tools & services shared across {analyticsData?.overview?.activeProjects || 0} active projects
              </p>
              
              {user?.globalSubscriptions?.length > 0 ? (
                <table className="dev-profile__subscriptions-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Started</th>
                      <th>Cost</th>
                      <th>Total Spent</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {user.globalSubscriptions.map((sub) => {
                      const TypeIcon = typeIcons[sub.type] || MoreHorizontal;
                      const monthlyCost = sub.billingCycle === 'yearly' ? sub.monthlyCost / 12 : sub.monthlyCost;
                      
                      // Calculate total spent based on start date and monthly cost
                      const calculateTotalSpent = () => {
                        if (!sub.startDate) return null;
                        const start = new Date(sub.startDate);
                        const now = new Date();
                        // Calculate months difference (inclusive of current month)
                        const monthsDiff = (now.getFullYear() - start.getFullYear()) * 12 + 
                                           (now.getMonth() - start.getMonth());
                        return Math.max(0, monthsDiff + 1) * monthlyCost;
                      };
                      
                      const totalSpent = calculateTotalSpent();
                      
                      return (
                        <tr key={sub._id} className={!sub.isActive ? 'inactive' : ''}>
                          <td>
                            <div className="dev-profile__sub-name-cell">
                              <TypeIcon size={14} />
                              <span>{sub.name}</span>
                            </div>
                          </td>
                          <td><span className="dev-profile__sub-type-badge">{sub.type}</span></td>
                          {/* Date Started column - inline editable */}
                          <td onClick={() => !inlineEdit.id && handleInlineEditStart(sub, 'startDate')}>
                            {inlineEdit.id === sub._id && inlineEdit.field === 'startDate' ? (
                              <input
                                type="date"
                                className="dev-profile__inline-input"
                                value={inlineEdit.value}
                                onChange={(e) => setInlineEdit({ ...inlineEdit, value: e.target.value })}
                                onBlur={() => handleInlineEditSave(sub)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleInlineEditSave(sub);
                                  if (e.key === 'Escape') handleInlineEditCancel();
                                }}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <span className="dev-profile__sub-date dev-profile__sub-date--editable">
                                {sub.startDate 
                                  ? new Date(sub.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                                  : '—'
                                }
                              </span>
                            )}
                          </td>
                          {/* Cost column - inline editable */}
                          <td onClick={() => !inlineEdit.id && handleInlineEditStart(sub, 'monthlyCost')}>
                            {inlineEdit.id === sub._id && inlineEdit.field === 'monthlyCost' ? (
                              <input
                                type="number"
                                className="dev-profile__inline-input"
                                value={inlineEdit.value}
                                onChange={(e) => setInlineEdit({ ...inlineEdit, value: e.target.value })}
                                onBlur={() => handleInlineEditSave(sub)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleInlineEditSave(sub);
                                  if (e.key === 'Escape') handleInlineEditCancel();
                                }}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <span className="dev-profile__sub-cost dev-profile__sub-cost--editable">
                                ${monthlyCost.toFixed(0)}/mo
                              </span>
                            )}
                          </td>
                          {/* Total Spent column - calculated from months × monthly cost */}
                          <td>
                            <span className="dev-profile__sub-total">
                              {totalSpent !== null ? `$${totalSpent.toFixed(0)}` : '—'}
                            </span>
                          </td>
                          {/* Actions - ON/OFF toggle, Pause/Play button, Delete */}
                          <td>
                            <div className="dev-profile__sub-actions">
                              <button 
                                onClick={() => handleToggleSubscription(sub)} 
                                className={`dev-profile__sub-toggle ${sub.isActive ? 'active' : ''}`} 
                                title={sub.isActive ? 'Pause' : 'Activate'}
                              >
                                {sub.isActive ? 'ON' : 'OFF'}
                              </button>
                              {/* Pause/Play icon button - replaces Edit button */}
                              <button 
                                onClick={() => handleToggleSubscription(sub)} 
                                className="dev-profile__sub-pause"
                                title={sub.isActive ? 'Pause subscription' : 'Resume subscription'}
                              >
                                {sub.isActive ? <Pause size={12} /> : <Play size={12} />}
                              </button>
                              <button onClick={() => handleDeleteSubscription(sub._id)} title="Delete">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="dev-profile__subscriptions-empty">
                  <p>No subscriptions added yet</p>
                </div>
              )}
              
              <div className="dev-profile__source-actions">
                <button className="btn-primary btn-sm" onClick={handleAddSubscription}>
                  <Plus size={12} /> Add Subscription
                </button>
              </div>
            </div>

            {/* Cursor AI Upload Card */}
            <div className="dev-profile__source-card" ref={uploadSectionRef}>
              <div className="dev-profile__source-card-header">
                <Bot size={16} />
                <h4>Cursor AI Billing</h4>
                {cursorUsage?.lastUploadedAt ? (
                  <span className="dev-profile__source-status dev-profile__source-status--active">
                    ✓ {getLastUploadText()}
                  </span>
                ) : (
                  <span className="dev-profile__source-status dev-profile__source-status--missing">
                    ⚠ No data
                  </span>
                )}
              </div>
              
              <p className="dev-profile__source-description">
                Upload your Cursor billing CSV to track AI token usage and costs
              </p>
              
              <CursorUsageUpload onDataRefresh={handleDataRefresh} />
              
              <div className="dev-profile__source-help">
                <span>Export from Cursor Settings → Billing → Download CSV</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubModal}
        onClose={() => setShowSubModal(false)}
        onSave={handleSaveSubscription}
        subscription={editingSub}
      />
    </div>
  );
};

export default DevProfile;

